import { Lock, Menu, WifiOff } from 'lucide-react'

import { CreditsPill } from '@/components/billing/CreditsPill'
import { useSystemStatus } from '@/features/system/useSystemStatus'
import { AccountMenu } from './AccountMenu'
import { ProductMark } from './Sidebar'
import { StatusPill } from './StatusPill'
import { env } from '@/config/env'

/**
 * The application bar.
 *
 * Laid out the way a console's bar is: where you are on the left, how the system is on the
 * right, and who you are in the corner. Everything in the status cluster comes from one
 * backend request — the frontend never probes GitHub or the database itself; it could not,
 * and pretending otherwise would put a credential in the browser.
 */
export function TopBar({ onMenuClick }: { onMenuClick?: () => void }) {
  const { data, isLoading, isError } = useSystemStatus()

  return (
    <header className="flex h-14 shrink-0 items-center justify-between gap-4 border-b border-line bg-surface px-3 sm:px-4">
      <div className="flex min-w-0 items-center gap-1.5 sm:gap-2">
        <button
          type="button"
          onClick={onMenuClick}
          className="mr-0.5 rounded-md p-1.5 text-ink-muted hover:bg-hover hover:text-ink md:hidden"
          aria-label="Open menu"
        >
          <Menu aria-hidden="true" className="h-5 w-5" />
        </button>
        <ProductMark className="h-6 w-6 text-ink md:hidden" />
        <span className="ml-0.5 truncate text-base font-semibold text-ink">RepoPilot</span>
        {env.appEnv !== 'production' ? (
          <span
            className="ml-1 inline-flex items-center gap-1.5 rounded-full border border-line-strong
              bg-raised px-2.5 py-0.5 text-xs font-medium capitalize text-ink-dim"
            title="The environment this deployment runs in"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-waiting" aria-hidden="true" />
            {env.appEnv}
          </span>
        ) : null}
        {data?.read_only_mode ? (
          <span
            className="inline-flex items-center gap-1 rounded-full border border-denied/25 bg-denied-wash
              px-2.5 py-0.5 text-xs font-medium text-denied"
            title="READ_ONLY_MODE is on. Every write tool is refused before it reaches GitHub."
          >
            <Lock aria-hidden="true" className="h-3 w-3" />
            read-only
          </span>
        ) : null}
      </div>

      <div className="flex min-w-0 items-center gap-3">
        {/* No scroll container anywhere in this bar. The account menu once sat inside an
            `overflow-x-auto` strip and was clipped by it — `overflow-x: auto` forces the other
            axis to scroll too, so anything absolutely positioned below the bar vanished. The
            status tooltips hang below the bar the same way, so the rule holds for them too. */}
        {isError ? (
          // Kept at every width. Four healthy pills are ambient detail a phone can do
          // without; "the API is unreachable" explains why the page is empty.
          <span
            className="flex shrink-0 items-center gap-1.5 rounded-full border border-fail/25
              bg-fail-wash px-2.5 py-1 text-xs font-medium text-fail"
          >
            <WifiOff aria-hidden="true" className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Cannot reach the API</span>
            <span className="sm:hidden">Offline</span>
          </span>
        ) : (
          <div className="hidden items-center gap-2 md:flex" aria-label="System status">
            <StatusPill label="backend" status={data?.backend} loading={isLoading} />
            <StatusPill label="database" status={data?.database} loading={isLoading} />
            <StatusPill label="GitHub" status={data?.github} loading={isLoading} />
            <StatusPill label="MCP" status={data?.mcp} loading={isLoading} />
          </div>
        )}
        <span className="hidden h-6 w-px bg-line md:block" aria-hidden="true" />
        {/* Kept at every width, unlike the status pills: how much is left is the one number
            a person needs while they are spending it. Renders nothing when billing is off. */}
        <CreditsPill />
        <div className="shrink-0">
          <AccountMenu />
        </div>
      </div>
    </header>
  )
}
